/**
 * Number Input with Chevrons Up/Down Renderer
 *
 * Number input with increment/decrement buttons stacked vertically
 */

import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import * as LucideIcons from 'lucide-react';
import type { FieldRenderer } from '@/MyCode/core';

/**
 * Number with Chevrons Up/Down Renderer
 * Features:
 * - Chevron Up button on top, Chevron Down on bottom
 * - Manual input in center
 * - Min/max/step constraints
 * - Disabled states when at limits
 */
export const renderNumberWithChevronsUpDown: FieldRenderer = ({ field, value, hasError, handleChange, isRequired }) => {
    const numericValue = typeof value === 'number' ? value : (value ? parseFloat(value as string) : 0);
    const step = field.step || 1;
    const minValue = typeof field.minValue === 'number' ? field.minValue : 0;
    const maxValue = typeof field.maxValue === 'number' ? field.maxValue : Infinity;

    const handleIncrement = () => {
        const newValue = Math.min(numericValue + step, maxValue);
        handleChange(field.name, newValue);
    };

    const handleDecrement = () => {
        const newValue = Math.max(numericValue - step, minValue);
        handleChange(field.name, newValue);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (val === '' || val === '-') {
            handleChange(field.name, val);
        } else {
            const numVal = parseFloat(val);
            if (!isNaN(numVal)) {
                handleChange(field.name, numVal);
            }
        }
    };

    const ChevronUpIcon = LucideIcons.ChevronUp;
    const ChevronDownIcon = LucideIcons.ChevronDown;

    return (
        <div className="space-y-2">
            <Label htmlFor={field.name}>
                {field.label}
                {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            <div className="relative inline-flex h-9 w-full items-center overflow-hidden rounded-md border border-input text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50 data-[error=true]:focus-within:border-destructive data-[error=true]:focus-within:ring-destructive/20" data-error={hasError}>
                <Input
                    id={field.name}
                    type="number"
                    value={value ?? ''}
                    onChange={handleInputChange}
                    min={minValue}
                    max={maxValue}
                    step={step}
                    className="flex-1 bg-background px-3 py-2 tabular-nums border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <div className="flex h-[calc(100%+2px)] flex-col">
                    <Button
                        type="button"
                        onClick={handleIncrement}
                        disabled={numericValue >= maxValue}
                        className="-me-px flex h-1/2 w-6 flex-1 items-center justify-center border border-input bg-background text-sm text-muted-foreground/80 transition-[color,box-shadow] hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <ChevronUpIcon size={12} aria-hidden="true" />
                    </Button>
                    <Button
                        type="button"
                        onClick={handleDecrement}
                        disabled={numericValue <= minValue}
                        className="-me-px -mt-px flex h-1/2 w-6 flex-1 items-center justify-center border border-input bg-background text-sm text-muted-foreground/80 transition-[color,box-shadow] hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <ChevronDownIcon size={12} aria-hidden="true" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default renderNumberWithChevronsUpDown;
